import { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { modelLabel } from '../billingModels';

const W = { bg:'#0A1526', surface:'#16233F', border:'#28395C', muted:'#7d8ea2', bright:'#f1f5f9', accent:'#00A0C8', danger:'#fc8181' };
const inp = { padding:'9px 12px', borderRadius:'7px', border:`1px solid ${W.border}`, background:W.bg, color:W.bright, fontSize:'14px', width:'100%', boxSizing:'border-box', outline:'none' };
const lbl = { fontSize:'11px', color:W.muted, fontWeight:'600', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'6px' };

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
function periodYears() { const y = new Date().getFullYear(); return [y-2, y-1, y, y+1]; }
function MonthYearPicker({ value, onChange, style }) {
  const [y, m] = value ? value.split('-') : [String(new Date().getFullYear()), String(new Date().getMonth()+1).padStart(2,'0')];
  const sel = { ...style, width:'auto', flex:1 };
  return (
    <div style={{ display:'flex', gap:'8px' }}>
      <select style={sel} value={m} onChange={e => onChange(`${y}-${e.target.value}`)}>
        {MONTHS.map((name, i) => { const val = String(i+1).padStart(2,'0'); return <option key={val} value={val}>{name}</option>; })}
      </select>
      <select style={sel} value={y} onChange={e => onChange(`${e.target.value}-${m}`)}>
        {periodYears().map(yr => <option key={yr} value={yr}>{yr}</option>)}
      </select>
    </div>
  );
}

// Labels come from ../billingModels -- this file used to keep its own copy.
const MODEL_COLORS = { hourly:{ bg:'rgba(0,160,200,0.15)', text:'#00A0C8' }, retainer:{ bg:'rgba(72,187,120,0.15)', text:'#48bb78' }, hybrid:{ bg:'rgba(237,137,54,0.15)', text:'#ed8936' } };
const TERMS_OPTIONS = ['Net 15', 'Net 30', 'Net 45', 'Net 60', 'Due on Receipt'];

function parseDueDays(terms) { const m = (terms || '').match(/(\d+)/); return m ? parseInt(m[1]) : 30; }
function addDays(dateStr, days) { const d = new Date(dateStr); d.setDate(d.getDate() + days); return d.toISOString().slice(0, 10); }

export default function NewInvoiceWizard({ org, clients, onClose, onRefresh, onNavigate }) {
  const STEPS = ['Select Client', 'Period & Terms', 'Confirm'];
  const [step, setStep]     = useState(0);
  const [saving, setSaving] = useState(false);
  const [err, setErr]       = useState('');
  const [search, setSearch] = useState('');

  const [selectedClient, setSelectedClient] = useState(null);

  const today        = new Date().toISOString().slice(0, 10);
  const defaultPeriod = today.slice(0, 7);
  const [period, setPeriod]             = useState(defaultPeriod);
  const [issuedDate, setIssuedDate]     = useState(today);
  const [paymentTerms, setPaymentTerms] = useState(org.invoice_payment_terms || 'Net 30');
  const [dueDate, setDueDate]           = useState(addDays(today, parseDueDays(org.invoice_payment_terms || 'Net 30')));
  const [lineItemMode, setLineItemMode] = useState(org.invoice_line_item_mode || 'by_task');

  const activeClients = (clients || []).filter(c => c.is_active !== false && !c.is_internal);
  const filtered = search
    ? activeClients.filter(c => c.name.toLowerCase().includes(search.toLowerCase()))
    : activeClients;

  const canContinue = () => {
    if (step === 0) return !!selectedClient;
    if (step === 1) return !!period && !!issuedDate;
    return true;
  };

  // Which billing terms cover this period. Loaded here as well as in
  // InvoiceModal because this wizard creates invoices on its own path --
  // instrumenting only the other one would leave the primary route blind.
  const [periodTerms, setPeriodTerms] = useState(null);
  useEffect(() => {
    const cid = selectedClient?.id;
    if (!cid || !period) { setPeriodTerms(null); return; }
    let canceled = false;
    (async () => {
      const { data, error } = await supabase.rpc('billing_terms_for_period',
        { p_client_id: cid, p_period: period });
      if (!canceled) setPeriodTerms(error ? null : (data || []));
    })();
    return () => { canceled = true; };
  }, [selectedClient, period]);

  const termsBanner = (() => {
    if (!periodTerms) return null;
    if (periodTerms.length === 0) return { bad: true, text:
      `No billing terms on record cover ${period}. This invoice will be priced from the client's cached rate rather than an agreed billing period.` };
    if (periodTerms.length > 1) return { bad: false, text:
      `Billing terms changed during ${period} — ${periodTerms.length} periods apply, so this invoice spans a rate change. Check the line items before sending.` };
    return null;
  })();

  const create = async () => {
    setErr('');
    setSaving(true);
    // Number it off the invoice's own billing period, not off today -- see
    // the migration note on generate_invoice_number for why that matters.
    const { data: numData, error: numErr } = await supabase.rpc('generate_invoice_number', { p_org_id: org.id, p_period_start: `${period}-01` });
    if (numErr) { setErr(numErr.message); setSaving(false); return; }

    // Seed subtotal for retainer/hybrid so the draft doesn't open at $0
    const retainerAmount = (selectedClient.model === 'retainer' || selectedClient.model === 'hybrid')
      ? parseFloat(selectedClient.retainer || 0)
      : 0;

    const { data: invData, error } = await supabase.from('invoices').insert({
      org_id: org.id,
      client_id: selectedClient.id,
      invoice_number: numData,
      period,
      status: 'draft',
      line_item_mode: lineItemMode,
      issued_date: issuedDate,
      due_date: dueDate || null,
      payment_terms: paymentTerms,
      subtotal: retainerAmount,
      total: retainerAmount,
      // Only when exactly one terms row covers the period; ambiguous or
      // missing terms record nothing rather than an arbitrary row.
      billing_terms_id: (periodTerms && periodTerms.length === 1) ? periodTerms[0].id : null,
    }).select('id').single();
    setSaving(false);
    if (error) { setErr(error.message); return; }

    // Insert the retainer line item so it shows up in the editor
    if (retainerAmount > 0 && invData?.id) {
      const monthName = period
        ? new Date(period + '-02').toLocaleString('default', { month: 'long', year: 'numeric' })
        : period;
      await supabase.from('invoice_line_items').insert({
        invoice_id: invData.id,
        description: `Monthly Retainer — ${monthName}`,
        hours: null,
        rate: null,
        amount: retainerAmount,
        sort_order: 0,
      });
    }

    onRefresh();
    onNavigate('invoices');
    onClose();
  };

  const modelBadge = (model) => {
    const c = MODEL_COLORS[model] || MODEL_COLORS.hourly;
    return { padding:'2px 8px', borderRadius:'4px', fontSize:'10px', fontWeight:'700', background:c.bg, color:c.text };
  };

  const stepContent = [
    <div key="client">
      <input style={{ ...inp, marginBottom:'12px' }} placeholder="Search clients…" value={search}
        onChange={e => setSearch(e.target.value)} autoFocus />
      <div style={{ maxHeight:'340px', overflowY:'auto', display:'flex', flexDirection:'column', gap:'6px' }}>
        {filtered.length === 0 && (
          <div style={{ color:W.muted, fontSize:'13px', padding:'20px', textAlign:'center' }}>No clients found.</div>
        )}
        {filtered.map(c => {
          const active = selectedClient?.id === c.id;
          return (
            <div key={c.id} onClick={() => setSelectedClient(c)}
              style={{ padding:'12px 14px', borderRadius:'8px', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'space-between', transition:'border-color 0.15s,background 0.15s',
                border:`1px solid ${active ? W.accent : W.border}`,
                background: active ? 'rgba(0,160,200,0.08)' : W.bg }}>
              <span style={{ fontSize:'14px', fontWeight:'600', color:W.bright }}>{c.name}</span>
              <span style={modelBadge(c.model)}>{modelLabel(c.model, 'label')}</span>
            </div>
          );
        })}
      </div>
    </div>,

    <div key="period" style={{ display:'flex', flexDirection:'column', gap:'14px' }}>
      <div>
        <div style={lbl}>Billing Period</div>
        <MonthYearPicker value={period} onChange={setPeriod} style={inp} />
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px' }}>
        <div>
          <div style={lbl}>Issued Date</div>
          <input style={inp} type="date" value={issuedDate} onChange={e => {
            setIssuedDate(e.target.value);
            setDueDate(addDays(e.target.value, parseDueDays(paymentTerms)));
          }} />
        </div>
        <div>
          <div style={lbl}>Due Date</div>
          <input style={inp} type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} />
        </div>
      </div>
      <div>
        <div style={lbl}>Payment Terms</div>
        <select style={inp} value={paymentTerms} onChange={e => {
          setPaymentTerms(e.target.value);
          setDueDate(addDays(issuedDate, parseDueDays(e.target.value)));
        }}>
          {TERMS_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>
      <div>
        <div style={lbl}>Line Item Mode</div>
        <select style={inp} value={lineItemMode} onChange={e => setLineItemMode(e.target.value)}>
          <option value="by_task">By Task Type</option>
          <option value="by_entry">By Time Entry</option>
          <option value="manual">Manual</option>
        </select>
      </div>
    </div>,

    <div key="confirm">
      <div style={{ padding:'16px', borderRadius:'9px', border:`1px solid ${W.border}`, background:W.bg, marginBottom:'14px' }}>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'14px' }}>
          <div><div style={lbl}>Client</div><div style={{ fontSize:'13px', color:W.bright, fontWeight:'600' }}>{selectedClient?.name}</div></div>
          <div><div style={lbl}>Model</div><div style={{ fontSize:'13px', color:W.bright }}>{modelLabel(selectedClient?.model, 'label')}</div></div>
          <div><div style={lbl}>Billing Period</div><div style={{ fontSize:'13px', color:W.bright }}>{period}</div></div>
          <div><div style={lbl}>Payment Terms</div><div style={{ fontSize:'13px', color:W.bright }}>{paymentTerms}</div></div>
          <div><div style={lbl}>Issued</div><div style={{ fontSize:'13px', color:W.bright }}>{issuedDate}</div></div>
          <div><div style={lbl}>Due</div><div style={{ fontSize:'13px', color:W.bright }}>{dueDate || '—'}</div></div>
        </div>
      </div>
      <div style={{ fontSize:'12px', color:W.muted, lineHeight:'1.5' }}>
        A draft invoice will be created and opened in the Invoices tab. You can add line items and edit all fields in the invoice editor.
      </div>
      {err && <div style={{ fontSize:'12px', color:W.danger, marginTop:'10px' }}>{err}</div>}
      {termsBanner && <div style={{ fontSize:'12px', marginTop:'10px', padding:'8px 10px', borderRadius:'6px',
        color: termsBanner.bad ? W.danger : '#f6c26b',
        background: termsBanner.bad ? 'rgba(229,62,62,0.08)' : 'rgba(246,194,107,0.10)',
        border: `1px solid ${termsBanner.bad ? 'rgba(229,62,62,0.25)' : 'rgba(246,194,107,0.30)'}` }}>{termsBanner.text}</div>}
    </div>,
  ];

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.75)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1200, padding:'20px' }}>
      <div style={{ background:W.surface, border:`1px solid ${W.border}`, borderRadius:'14px', width:'100%', maxWidth:'520px', maxHeight:'90vh', display:'flex', flexDirection:'column', boxShadow:'0 20px 60px rgba(0,0,0,0.6)' }}>
        <div style={{ padding:'22px 24px 16px', borderBottom:`1px solid ${W.border}`, flexShrink:0 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
            <div>
              <div style={{ fontSize:'17px', fontWeight:'800', color:W.bright }}>New Invoice</div>
              <div style={{ fontSize:'12px', color:W.muted, marginTop:'2px' }}>Step {step + 1} of {STEPS.length} — {STEPS[step]}</div>
            </div>
            <button onClick={onClose} style={{ background:'none', border:'none', color:W.muted, fontSize:'20px', cursor:'pointer', lineHeight:1, padding:'2px' }}>×</button>
          </div>
          <div style={{ marginTop:'14px', height:'4px', borderRadius:'2px', background:W.border, overflow:'hidden' }}>
            <div style={{ height:'100%', borderRadius:'2px', background:W.accent, width:`${((step + 1) / STEPS.length) * 100}%`, transition:'width 0.3s' }} />
          </div>
        </div>

        <div style={{ padding:'22px 24px', overflowY:'auto', flex:1 }}>
          {stepContent[step]}
        </div>

        <div style={{ padding:'16px 24px', borderTop:`1px solid ${W.border}`, display:'flex', justifyContent:'space-between', alignItems:'center', flexShrink:0 }}>
          {step > 0
            ? <button onClick={() => { setErr(''); setStep(s => s - 1); }}
                style={{ padding:'8px 18px', borderRadius:'7px', border:`1px solid ${W.border}`, background:'transparent', color:W.bright, fontSize:'13px', fontWeight:'600', cursor:'pointer' }}>← Back</button>
            : <div />
          }
          {step < STEPS.length - 1
            ? <button onClick={() => canContinue() && setStep(s => s + 1)} disabled={!canContinue()}
                style={{ padding:'8px 22px', borderRadius:'7px', border:'none', fontSize:'13px', fontWeight:'600', transition:'background 0.15s',
                  background: canContinue() ? W.accent : W.border, color: canContinue() ? '#fff' : W.muted, cursor: canContinue() ? 'pointer' : 'default' }}>
                Continue →
              </button>
            : <button onClick={create} disabled={saving}
                style={{ padding:'8px 22px', borderRadius:'7px', border:'none', fontSize:'13px', fontWeight:'600',
                  background: saving ? W.border : W.accent, color: saving ? W.muted : '#fff', cursor: saving ? 'default' : 'pointer' }}>
                {saving ? 'Creating…' : 'Create Draft Invoice'}
              </button>
          }
        </div>
      </div>
    </div>
  );
}
